import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { deriveCapabilities, can as canHelper } from '@/lib/capabilities';
import type { ProducerCapabilities, ProducerCapabilityKey } from '@/lib/capabilities';

const QUERY_KEY = 'producer-capabilities';

/**
 * Central hook: fetches the producer_capabilities_v view
 * and derives all capability flags + checklist.
 */
export function useProducerCapabilities(producerId: string | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [QUERY_KEY, producerId],
    queryFn: async (): Promise<ProducerCapabilities | null> => {
      if (!producerId) return null;

      const { data, error } = await supabase
        .from('producer_capabilities_v')
        .select('*')
        .eq('producer_id', producerId)
        .maybeSingle();

      if (error) {
        console.error('Failed to fetch producer capabilities:', error);
        throw error;
      }

      if (!data) return null;

      return deriveCapabilities(data);
    },
    enabled: !!producerId,
    staleTime: 30_000, // 30s – view is cheap
  });

  /** Convenience: check a single capability */
  const hasCapability = (key: ProducerCapabilityKey): boolean =>
    canHelper(query.data?.capabilities, key);

  /** Force refetch (e.g. after publish/unpublish) */
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY, producerId] });

  return {
    data: query.data,
    meta: query.data?.meta ?? null,
    checklist: query.data?.checklist ?? null,
    capabilities: query.data?.capabilities ?? null,
    hasCapability,
    invalidate,
    isLoading: query.isLoading,
    error: query.error,
  };
}
